import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signedInAs } from "../../lib/session";
import AuthService from "../../../../services/authService";
import { UploadService } from "../../../../services/uploadService";
import { LEGAL_VERSION } from "../../../../constants/legal";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/Toast";
import { Button } from "../../components/primitives";
import { compressForAvatar, pickFiles, releaseUri } from "../../lib/files";
import { carryFrom, msg } from "./authUtils";
import { DetailsStep, EmailStep, NameStep, PasswordStep, PhotoStep } from "./SignupSteps";

const NAMES = ["Your name", "UM email", "Password", "Details", "Profile photo"];
const TITLES = ["What should we call you?", "Your UM email", "Create a password", "What kind of student are you?", "Almost Done!"];
const SUBS = [
  "Your name shows up when you join clubs.",
  "Use your @miami.edu address so we can verify you.",
  "Make it at least 6 characters.",
  "Fill out your major and class year!",
  "Add a profile photo so your clubs recognize you.",
];

/** 5-step sign-up wizard mirroring screens/SignUpScreen.js (:43-46, :153-201, handleSignUp). */
export default function SignupCard({ busyRef }) {
  const keep = carryFrom(useLocation().state);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [major, setMajor] = useState("");
  const [gradYear, setGradYear] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [picking, setPicking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const photoRef = useRef(null);
  photoRef.current = photo;
  const uploadingRef = useRef(false);
  // The photo URI outlives the card while the post-signup upload is still reading it (released in its finally).
  useEffect(() => () => { if (photoRef.current && !uploadingRef.current) releaseUri(photoRef.current); }, []);

  const v = { firstName, lastName, email, password, confirm, major, gradYear, accepted };
  const set = { setFirstName, setLastName, setEmail, setPassword, setConfirm, setMajor, setGradYear, setAccepted };

  const requireTerms = () => {
    if (accepted) return true;
    setError("You must accept the Terms & Conditions and Privacy Policy to continue.");
    return false;
  };

  const next = () => {
    setError("");
    if (step === 0) {
      if (!firstName.trim() || !lastName.trim()) { setError("Please enter your first and last name to continue."); return; }
    } else if (step === 1) {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed || trimmed.split("@")[1] !== "miami.edu") {
        setError("Only University of Miami email addresses (@miami.edu) are accepted.");
        return;
      }
      setEmail(trimmed);
    } else if (step === 2) {
      if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
      if (password !== confirm) { setError("Please make sure both passwords match."); return; }
    } else if (step === 3) {
      if (!requireTerms()) return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };

  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };
  const skipDetails = () => { setError(""); if (requireTerms()) setStep(4); };

  const takeFile = async (file) => {
    if (!file || picking || busyRef.current) return; // submit owns the current photo until it settles
    setPicking(true);
    setError("");
    try {
      const uri = await compressForAvatar(file);
      if (photoRef.current) releaseUri(photoRef.current);
      setPhoto(uri);
    } catch (e) {
      setError(msg(e, "We couldn't open that photo. Please try again."));
    } finally {
      setPicking(false);
    }
  };
  const pick = async () => { const [f] = await pickFiles({ accept: "image/*" }); await takeFile(f); };
  const clearPhoto = () => { if (busyRef.current) return; if (photo) releaseUri(photo); setPhoto(null); };

  // photoUri: picked blob URI, or null (Skip this -- even if a photo was picked).
  const submit = async (photoUri) => {
    if (loading || picking || !requireTerms()) return; // a photo still compressing would be dropped
    setError("");
    setLoading(true);
    busyRef.current = true;
    if (photoUri) uploadingRef.current = true; // no unmount path may revoke the blob before the upload settles
    try {
      const userData = {
        full_name: `${firstName.trim()} ${lastName.trim()}`,
        university: "University of Miami",
        ...(major.trim() ? { major: major.trim() } : {}),
        ...(gradYear.trim() ? { graduation_year: parseInt(gradYear, 10) || null } : {}),
        terms_accepted: true,
        legal_version: LEGAL_VERSION,
      };
      const result = await signUp(email, password, userData);
      if (!result.success) {
        uploadingRef.current = false;
        setError(result.error || "An error occurred during sign up. Please try again.");
        return;
      }
      navigate("/auth/verify", { replace: true, state: { ...keep, verificationEmailFailed: result.verificationEmailFailed === true } });
      if (photoUri) {
        // Account exists now; a photo failure never undoes the signup (SignUpScreen.js:310).
        try {
          // Bound to the account this signup created: if the browser switched accounts meanwhile,
          // the photo is never uploaded or written under the other account.
          const uid = result.uid;
          if (!signedInAs(uid)) throw new Error("Signed-in account changed after signup");
          const avatarUrl = await UploadService.uploadProfilePicture(photoUri, uid);
          if (!signedInAs(uid)) throw new Error("Signed-in account changed after signup");
          const { error: avatarError } = await AuthService.updateUserProfile(uid, { avatar_url: avatarUrl });
          if (avatarError) throw avatarError;
        } catch {
          toast("We couldn't upload your photo. You can add it later from your profile.", "alert");
        } finally {
          uploadingRef.current = false;
          releaseUri(photoUri);
        }
      }
    } catch (e) {
      uploadingRef.current = false;
      setError(msg(e, "An error occurred during sign up. Please try again."));
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (step === 4) submit(photo);
    else next();
  };

  return (
    <>
      <div className="steps">{NAMES.map((n, i) => <i key={n} className={i <= step ? "on" : ""} />)}</div>
      <span className="label">Step {step + 1} of 5 · {NAMES[step]}</span>
      <h2 style={{ fontSize: 28, fontWeight: 800 }}>{TITLES[step]}</h2>
      <p className="muted">{SUBS[step]}</p>
      <form className="col" style={{ gap: 14 }} onSubmit={onSubmit} noValidate>
        {step === 0 && <NameStep v={v} set={set} />}
        {step === 1 && <EmailStep v={v} set={set} />}
        {step === 2 && <PasswordStep v={v} set={set} />}
        {step === 3 && <DetailsStep v={v} set={set} onSkip={skipDetails} />}
        {step === 4 && <PhotoStep photo={photo} picking={picking} locked={loading} onPick={pick} onFile={takeFile} onClear={clearPhoto} name={`${firstName} ${lastName}`.trim()} />}
        {error && <div className="err-text" role="alert">{error}</div>}
        <div className="row">
          {step > 0 && <Button variant="ghost" onClick={back} disabled={loading} data-testid="signUp.back">Back</Button>}
          <Button variant="pri" className="grow" type="submit" disabled={loading || (step === 4 && picking)} data-testid="signUp.primaryCta">
            {step === 4 ? (loading ? "Creating account…" : "Create account") : "Continue"}
          </Button>
        </div>
        {step === 4 && (
          <button type="button" className="link" style={{ alignSelf: "center" }} onClick={() => submit(null)} disabled={loading || picking} data-testid="signUp.photo.skip">Skip for now</button>
        )}
      </form>
      <p className="meta" style={{ textAlign: "center" }}>
        Have an account? <Link className="link" to="/auth/login" state={keep} data-testid="signUp.switchMode">Sign in</Link>
      </p>
    </>
  );
}
